/**
 * **같은 대화에서 이미 읽은 것은 다시 읽지 않는다.**
 *
 * 「만 10년인데 연차 늘어날까?」 → 「그럼 내년에 생겨?」에서 앞 턴에 읽은 2025·2026년 연차생성을 또 조회했다
 * (23초+5초, 실제 사용 2026-10-22). 드라이버는 같은 화면·조건이 **지금 떠 있을 때만** 목록을 다시 쓴다 — 조건을
 * 바꿔 가며 여러 번 읽었으면 마지막 것만 떠 있다. 런타임은 페이지가 살아 있는 동안 남으므로 여기 기억해 둔다.
 *
 * 다시 읽는 때:
 * - 후속 질문이 아니면(새 대화) — 벤치처럼 한 페이지에서 질문을 이어 하면 화면이 안 열려 판정이 엇갈린다.
 * - `force`(「다시 조회」「최신」, 다시 읽기 재시도) — 읽고 기억을 새로 쓴다.
 * - 쓰기(저장·결재·보내기·첨부·조작) 뒤 — 기억을 비운다.
 * - 10분이 지나면. 행이 없던 조회는 기억하지 않는다 — 화면이 늦게 그렸을 수 있다.
 */
const TTL_MS = 10 * 60_000;
const WRITES = ['approve', 'attach', 'send', 'operate', 'commit'];

type Request = { label: string; routePath: string; rowsStateKey?: string; conditions: Record<string, string>; force?: boolean };
type Result = { label: string; rows: unknown[]; answered?: boolean };

const memory = new Map<string, { at: number; result: Result }>();

export function forgetRetrievals(): void {
  memory.clear();
}

function keyOf(kind: string, request: Request & Record<string, unknown>): string {
  const { label: _label, force: _force, ...rest } = request;
  const conditions = Object.keys(request.conditions ?? {}).sort()
    .map((key) => `${key}=${String(request.conditions[key] ?? '')}`).join('&');
  return `${kind} ${JSON.stringify({ ...rest, conditions })}`;
}

export function rememberRetrievals<T extends object>(
  session: T,
  options: { followUp: boolean; now?: () => number },
): T {
  const now = options.now ?? Date.now;
  if (!options.followUp) memory.clear();
  const remembered = (kind: string, read: (request: Request) => Promise<Result>) => async (request: Request) => {
    const key = keyOf(kind, request as Request & Record<string, unknown>);
    const kept = memory.get(key);
    if (!request.force && kept && now() - kept.at <= TTL_MS) {
      // eslint-disable-next-line no-console
      console.debug('[agent] 앞서 읽은 것을 쓴다', request.routePath, request.label);
      return { ...kept.result, label: request.label };
    }
    const result = await read(request);
    if (result?.answered !== false && result?.rows?.length) memory.set(key, { at: now(), result });
    return result;
  };
  return new Proxy(session, {
    get(target, prop) {
      const value = Reflect.get(target, prop, target);
      if (typeof value !== 'function') return value;
      const bound = value.bind(target);
      if (prop === 'retrieve' || prop === 'sweepDetail') return remembered(prop, bound);
      if (WRITES.indexOf(String(prop)) >= 0) {
        return (...args: unknown[]) => { memory.clear(); return bound(...args); };
      }
      return bound;
    },
  });
}
